'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { IoSparkles } from 'react-icons/io5';
import { FiArrowRight, FiCode, FiTerminal } from 'react-icons/fi';
import { CgSmartphoneChip } from 'react-icons/cg';
import Image from 'next/image';
import type { HeroProps } from './types';
import { useContactModal } from '../ContactModal/ContactModalContext';
import { Button, AppLink } from '@/shared/ui/atoms';
import { StatCard, SectionCard } from '@/shared/ui/molecules';
import { heroMock } from '@/shared/store/mocks';
import styles from './hero.module.scss';

const HERO_FLOAT_ICONS: Record<(typeof heroMock.floatIconIds)[number], React.ComponentType<{ className?: string }>> = {
    code: FiCode,
    terminal: FiTerminal,
    chip: CgSmartphoneChip,
    sparkles: IoSparkles,
};

export default function Hero(props: Partial<HeroProps>) {
    const {
        welcomeLabel = heroMock.welcomeLabel,
        greeting = heroMock.greeting,
        name = heroMock.name,
        title: titleProp,
        description = heroMock.description,
        skills = heroMock.skills,
        primaryCta = heroMock.primaryCta,
        secondaryCta = heroMock.secondaryCta,
        stats = heroMock.stats,
    } = props;

    const { openModal: openContactModal } = useContactModal();
    const [typedText, setTypedText] = useState('');
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const fullText = titleProp ?? heroMock.title;

    const contentRef = useRef<HTMLDivElement>(null);
    const logoGlassRef = useRef<HTMLDivElement>(null);
    const ring1Ref = useRef<HTMLDivElement>(null);
    const ring2Ref = useRef<HTMLDivElement>(null);
    const logoFloatRef = useRef<HTMLDivElement>(null);
    const pulseRef = useRef<HTMLDivElement>(null);
    const sparkleRef = useRef<HTMLDivElement>(null);
    const logoGlowRef = useRef<HTMLDivElement>(null);
    const logoAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let i = 0;
        const t = setInterval(() => {
            if (i <= fullText.length) {
                setTypedText(fullText.slice(0, i));
                i++;
            } else clearInterval(t);
        }, 100);
        return () => clearInterval(t);
    }, [fullText]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const children = el.querySelectorAll('[data-animate]');
        gsap.fromTo(
            children,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, delay: 0.2, ease: 'power2.out' },
        );
    }, []);

    useEffect(() => {
        const el = logoAreaRef.current;
        if (!el) return;
        gsap.to(el, { opacity: 1, duration: 0.8, delay: 0.5, ease: 'power2.out' });
    }, []);

    useEffect(() => {
        if (sparkleRef.current) {
            gsap.to(sparkleRef.current, { rotation: 360, duration: 3, repeat: -1, ease: 'none' });
        }
    }, []);

    useEffect(() => {
        if (ring1Ref.current) gsap.to(ring1Ref.current, { rotation: 360, duration: 20, repeat: -1, ease: 'none' });
        if (ring2Ref.current) gsap.to(ring2Ref.current, { rotation: -360, duration: 25, repeat: -1, ease: 'none' });
    }, []);

    useEffect(() => {
        if (logoFloatRef.current) {
            gsap.to(logoFloatRef.current, {
                y: [0, -20, 0],
                duration: 4,
                repeat: -1,
                ease: 'sine.inOut',
            } as unknown as gsap.TweenVars);
        }
    }, []);

    useEffect(() => {
        if (pulseRef.current) {
            gsap.to(pulseRef.current, {
                scale: [1, 1.5, 1],
                opacity: [0.5, 0, 0.5],
                duration: 2,
                repeat: -1,
                ease: 'power2.out',
            } as unknown as gsap.TweenVars);
        }
    }, []);

    useEffect(() => {
        if (logoGlowRef.current) {
            gsap.to(logoGlowRef.current, {
                opacity: [0.5, 0.8, 0.5],
                duration: 3,
                repeat: -1,
                ease: 'sine.inOut',
            } as unknown as gsap.TweenVars);
        }
    }, []);

    const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x: x * 5, y: -y * 5 });
    };

    const particles = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        iconId: heroMock.floatIconIds[i % heroMock.floatIconIds.length],
    }));

    return (
        <section id="home" className={styles.hero}>
            <div className={styles.hero__container}>
                <div className={styles.hero__grid}>
                    <div ref={contentRef} className={styles.hero__content}>
                        <div data-animate className={styles.hero__welcomeBadge}>
                            <div ref={sparkleRef} className={styles.hero__welcomeIcon}>
                                <IoSparkles aria-hidden />
                            </div>
                            <span>{welcomeLabel}</span>
                        </div>
                        <h1 data-animate className={styles.hero__heading}>
                            <span className={styles.hero__greeting}>{greeting}</span>{' '}
                            <span className={styles.hero__name}>
                                {name}
                                <span className={styles.hero__nameGlitch} aria-hidden>
                                    {name}
                                </span>
                            </span>
                        </h1>
                        <div data-animate className={styles.hero__titleWrap}>
                            <h2 className={styles.hero__title}>
                                {typedText}
                                <span className={styles.hero__cursor} />
                            </h2>
                            <div className={styles.hero__titleGlow} aria-hidden />
                        </div>
                        <p data-animate className={styles.hero__description}>
                            {description}
                        </p>
                        <div data-animate className={styles.hero__skills}>
                            {skills.map(tech => (
                                <span key={tech} className={styles.hero__skillTag}>
                                    {tech}
                                </span>
                            ))}
                        </div>
                        <div data-animate className={styles.hero__ctas}>
                            <AppLink
                                href={primaryCta.href}
                                className={styles.hero__btnPrimary}
                                classNames={{
                                    text: styles.hero__btnPrimaryInner,
                                    endIcon: styles.hero__btnIcon,
                                }}
                                endIcon={<FiArrowRight aria-hidden />}
                            >
                                {primaryCta.label}
                            </AppLink>
                            {secondaryCta.href === '#contact' ? (
                                <Button onClick={openContactModal} className={styles.hero__btnSecondary}>
                                    {secondaryCta.label}
                                </Button>
                            ) : (
                                <AppLink href={secondaryCta.href} className={styles.hero__btnSecondary}>
                                    {secondaryCta.label}
                                </AppLink>
                            )}
                        </div>
                    </div>

                    <div
                        ref={logoAreaRef}
                        className={styles.hero__logoArea}
                        onMouseMove={onMouseMove}
                        role="presentation"
                    >
                        <div className={styles.hero__logoBox}>
                            {/* Кільця ззаду, щоб були видимі навколо logo area */}
                            <div ref={ring1Ref} className={styles.hero__ring} style={{ margin: '-10%' }} aria-hidden>
                                <div
                                    className={styles.hero__ringDot}
                                    style={{ top: '50%', left: 0, transform: 'translate(-50%, -50%)' }}
                                />
                            </div>
                            <div ref={ring2Ref} className={styles.hero__ring2} style={{ margin: '-15%' }} aria-hidden>
                                <div
                                    className={styles.hero__ringDot2}
                                    style={{ top: 0, left: '50%', transform: 'translate(-50%, -50%)' }}
                                />
                            </div>
                            <div
                                ref={logoGlassRef}
                                className={styles.hero__logoGlass}
                                style={{
                                    transform: `perspective(1000px) rotateY(${mousePosition.x}deg) rotateX(${mousePosition.y}deg)`,
                                }}
                            />
                            {particles.map(p => {
                                const Icon = HERO_FLOAT_ICONS[p.iconId];
                                return (
                                    <div key={p.id} className={styles.hero__floatParticle} aria-hidden>
                                        <Icon className={styles.hero__floatParticleIcon} />
                                    </div>
                                );
                            })}
                            <div className={styles.hero__logoInner}>
                                <div ref={logoGlowRef} className={styles.hero__logoGlow} aria-hidden />
                                <div ref={logoFloatRef} className={styles.hero__logoFloat}>
                                    <Image
                                        src={heroMock.logoSrc}
                                        alt="MT Logo"
                                        width={400}
                                        height={400}
                                        priority
                                        loading="eager"
                                        className={styles.hero__logoImg}
                                    />
                                </div>
                            </div>
                            <div ref={pulseRef} className={styles.hero__pulse}>
                                <div className={styles.hero__pulseInner} />
                            </div>
                            <div className={styles.hero__scanLine} aria-hidden />
                        </div>

                        <div className={styles.hero__stats}>
                            {stats.map(stat => (
                                <SectionCard
                                    key={stat.label}
                                    classNames={{
                                        root: styles.hero__statCard,
                                        hover: styles.hero__statCardHover,
                                        glow: styles.hero__statCardHover,
                                    }}
                                >
                                    <StatCard
                                        value={stat.value}
                                        label={stat.label}
                                        classNames={{
                                            root: styles.hero__statContent,
                                            value: styles.hero__statValue,
                                            label: styles.hero__statLabel,
                                        }}
                                    />
                                </SectionCard>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
